import React, { useState, useEffect, useRef } from "react";

// DISPLAY = headlines only · READ = everything you actually read · SANS = labels + buttons only
var PLAY  = '"Playfair Display", Georgia, serif';
var SERIF = '"Newsreader", Georgia, serif';
var SANS  = '"Outfit", Helvetica, sans-serif';

// Rolls Royce "Purple Spirit" palette
var T = {
  dark: {
    bg: "#0E0B18",
    grad: "radial-gradient(ellipse at 50% 20%, rgba(30,22,50,0.6) 0%, transparent 60%), #0E0B18",
    band: "#120F1E",
    text: "#E8E6E2",
    textSoft: "rgba(232,230,226,0.70)",
    textMuted: "rgba(232,230,226,0.52)",
    accent: "#B8967A",
    accentSoft: "rgba(198,168,140,0.88)",
    accentMuted: "rgba(184,150,122,0.22)",
    border: "rgba(232,230,226,0.10)",
    cardBg: "rgba(232,230,226,0.04)",
    backBg: "#17122A",
    phoneShell: "#080610",
    phoneScreen: "#151124",
    field: "rgba(232,230,226,0.05)",
    btnBg: "#E8E6E2",
    btnText: "#0E0B18",
    scrim: "rgba(8,6,16,0.90)",
    frameShadow: "0 8px 40px rgba(0,0,0,0.5), 0 2px 12px rgba(0,0,0,0.3)",
  },
  light: {
    bg: "#F8F5F0",
    grad: "radial-gradient(ellipse at 50% 20%, rgba(184,150,122,0.04) 0%, transparent 60%), #F8F5F0",
    band: "#F2EDE5",
    text: "#1A1720",
    textSoft: "rgba(26,23,32,0.68)",
    textMuted: "rgba(26,23,32,0.52)",
    accent: "#8B6F58",
    accentSoft: "rgba(139,111,88,0.92)",
    accentMuted: "rgba(139,111,88,0.22)",
    border: "rgba(26,23,32,0.10)",
    cardBg: "rgba(26,23,32,0.04)",
    backBg: "#EFE9E0",
    phoneShell: "#1A1720",
    phoneScreen: "#FBF8F3",
    field: "rgba(26,23,32,0.04)",
    btnBg: "#1A1720",
    btnText: "#F8F5F0",
    scrim: "rgba(26,23,32,0.80)",
    frameShadow: "0 8px 40px rgba(0,0,0,0.12), 0 2px 12px rgba(0,0,0,0.08)",
  },
};

var FONT_URL = 'https://fonts.googleapis.com/css2?family=Newsreader:ital,opsz,wght@0,6..72,300;0,6..72,400;0,6..72,500;1,6..72,300;1,6..72,400&family=Outfit:wght@300;400;500;600;700&family=Playfair+Display:ital,wght@0,400;0,500;1,400&display=swap';

var CTA = "Start a constellation";

var STYLES = [
  { file: "starlight", name: "Starlight", desc: "Platinum on midnight",
    back: "The house style. Six voices set around a constellation, silver on near-black. This is the one people order first." },
  { file: "grid", name: "Grid", desc: "One cell per person",
    back: "One cell per person, name and all. The only style that gets better as more people write — built for fifteen and up." },
  { file: "mosaic", name: "Mosaic", desc: "Their words form the letters",
    back: "Their initials, filled with everything everyone said. Step close and you can read it; step back and it's a monogram." },
  { file: "starmap", name: "Star Map", desc: "Gold on matte black",
    back: "The same constellation, warmer. Gold on matte black for someone whose wall already leans that way." },
  { file: "starsign", name: "Star Sign", desc: "Their words form their sign",
    back: "Their zodiac glyph, filled with the writing. The one to pick when the birthday is the whole point." },
  { file: "statement", name: "Statement", desc: "Newsprint collage",
    back: "Torn newsprint, mixed type, nothing centred. For the person who would hate anything that looks like a certificate." },
  { file: "custom", name: "Custom", desc: "Any colour, any typeface",
    back: "Choose the ground, the ink, the type and the layout yourself. For when none of the others is quite them." },
];

var ADJECTIVES = ["Warm", "Fearless", "Patient", "Stubborn", "Generous", "Steady", "Funny", "Loyal", "Sharp"];
var PICKED = { Warm: 1, Fearless: 1, Patient: 1 };

var STEPS = [
  { t: "Say something kind", s: "A memory, a moment, or what you'd say if someone handed you the mic." },
  { t: "Pick the words", s: "Up to six that describe them. The ones everyone picks rise to the top." },
  { t: "An inside joke, or a wish", s: "Something that'd make them laugh. Totally optional — most people skip it." },
];

var CSS = `
@keyframes cl-fade { from { opacity:0; transform:translateY(28px); } to { opacity:1; transform:translateY(0); } }
.cl-btn { transition: all 0.4s cubic-bezier(0.25,0.46,0.45,0.94) !important; cursor: pointer !important; }
.cl-btn:hover { transform: translateY(-2px) !important; }
.cl-card { transition: all 0.4s ease !important; }
.cl-card:hover { transform: translateY(-3px) !important; }

/* ── fanned poster deck ── */
.yt-deck { position: relative; height: 640px; overflow: hidden; cursor: grab; touch-action: pan-y; user-select: none; -webkit-user-select: none; }
.yt-deck.is-drag { cursor: grabbing; }
.yt-fan { position: absolute; left: 0; right: 0; top: 40px; height: 0; }
.yt-card {
  --w: 360px; display: block;
  position: absolute; left: 50%; top: 0;
  width: var(--w); margin-left: calc(var(--w) / -2);
  transform-origin: 50% 900px; perspective: 1400px;
  background: none; border: 0; padding: 0; cursor: pointer;
  transition: transform .6s cubic-bezier(.22,.68,.28,1), opacity .45s ease;
  will-change: transform;
}
.yt-inner {
  display: block; position: relative; width: 100%; aspect-ratio: 1 / 1;
  transition: transform .58s cubic-bezier(.34,.72,.32,1);
  transform-style: preserve-3d; -webkit-transform-style: preserve-3d;
}
.yt-card.is-flip .yt-inner { transform: rotateY(180deg); }
.yt-face {
  position: absolute; inset: 0;
  -webkit-backface-visibility: hidden; backface-visibility: hidden;
  -webkit-transform: translateZ(0); transition: opacity .11s ease .22s;
}
.yt-front { display: block; opacity: 1; border: 8px solid #111; box-sizing: border-box; line-height: 0; }
.yt-back {
  opacity: 0; transform: rotateY(180deg);
  border: 1px solid var(--yt-accentmuted); box-sizing: border-box;
  background: var(--yt-backbg);
  display: flex; flex-direction: column; justify-content: center;
  padding: 44px 40px; text-align: left;
}
.yt-card.is-flip .yt-front { opacity: 0; }
.yt-card.is-flip .yt-back { opacity: 1; }
.yt-front img { width: 100%; height: 100%; display: block; object-fit: cover; }
.yt-sheen {
  position: absolute; inset: 0; pointer-events: none; opacity: 0;
  transition: opacity .5s ease;
  background: linear-gradient(105deg, transparent 36%, rgba(255,255,255,.32) 50%, transparent 64%);
  mix-blend-mode: soft-light;
}
.yt-card.is-live .yt-sheen { opacity: 1; }
.yt-card.is-live .yt-inner { transition: transform .18s ease-out; }

/* ── lifted off the wall ── */
.yt-lift {
  position: fixed; inset: 0; z-index: 400;
  background: var(--yt-scrim); -webkit-backdrop-filter: blur(7px); backdrop-filter: blur(7px);
  display: flex; align-items: center; justify-content: center; gap: 56px;
  padding: 48px; opacity: 0; visibility: hidden; pointer-events: none;
  transition: opacity .45s ease;
}
.yt-lift.on { opacity: 1; visibility: visible; pointer-events: auto; }
.yt-lift-poster {
  width: min(62vh, 520px); aspect-ratio: 1 / 1; flex: none;
  border: 12px solid #111; box-sizing: border-box; line-height: 0;
  box-shadow: 0 50px 100px -30px rgba(0,0,0,.8);
  transform: scale(.93) translateY(16px);
  transition: transform .55s cubic-bezier(.2,.9,.3,1);
}
.yt-lift.on .yt-lift-poster { transform: none; }
.yt-lift-poster img { width: 100%; height: 100%; display: block; object-fit: cover; }
.yt-lift-text { max-width: 330px; text-align: left; }
.yt-close {
  position: absolute; top: 28px; right: 32px; z-index: 2;
  width: 44px; height: 44px; border-radius: 50%;
  background: none; border: 1px solid var(--yt-border);
  color: var(--yt-textsoft); font-size: 16px; cursor: pointer; font-family: inherit;
  display: flex; align-items: center; justify-content: center; transition: all .3s ease;
}
.yt-close:hover { border-color: var(--yt-accentsoft); color: var(--yt-text); }
@media (max-width: 900px) {
  .yt-lift { flex-direction: column; gap: 26px; padding: 76px 26px 40px; overflow-y: auto; justify-content: flex-start; }
  .yt-lift-poster { width: min(80vw, 400px); }
  .yt-lift-text { max-width: 440px; text-align: center; }
}

.yt-ctrl { position: relative; z-index: 5; display: flex; align-items: center; justify-content: center; gap: 18px; margin-top: -46px; }
.yt-arrow {
  width: 42px; height: 42px; border-radius: 50%;
  background: none; border: 1px solid var(--yt-border);
  color: var(--yt-textsoft); font-size: 15px; cursor: pointer;
  display: flex; align-items: center; justify-content: center;
  transition: all .3s ease; font-family: inherit;
}
.yt-arrow:hover { border-color: var(--yt-accentsoft); color: var(--yt-text); }
.yt-arrow:disabled { opacity: .25; cursor: default; }
.yt-dots { display: flex; gap: 8px; align-items: center; }
.yt-dot { width: 5px; height: 5px; border-radius: 50%; background: var(--yt-textmuted); border: 0; padding: 0; cursor: pointer; transition: all .3s ease; }
.yt-dot.on { background: var(--yt-accent); width: 20px; border-radius: 99px; }

/* ── contributor phone ── */
.yt-show { display: grid; grid-template-columns: 1fr 320px; gap: 64px; align-items: center; max-width: 1000px; margin: 68px auto 0; padding: 0 48px; }
.yt-steps { display: flex; flex-direction: column; gap: 4px; }
.yt-step {
  display: block; width: 100%; text-align: left; cursor: pointer;
  font-family: inherit; background: none; border: 0;
  border-left: 1px solid var(--yt-border);
  padding: 22px 26px; transition: all .35s ease;
}
.yt-step:hover { border-left-color: var(--yt-accentsoft); }
.yt-step.on { border-left: 1px solid var(--yt-accent); background: var(--yt-cardbg); }
.yt-phone {
  width: 300px; aspect-ratio: 9 / 18.5;
  background: var(--yt-shell); border-radius: 38px; padding: 9px;
  box-shadow: var(--yt-frameshadow); position: relative;
}
.yt-screen { width: 100%; height: 100%; border-radius: 30px; overflow: hidden; background: var(--yt-screen); position: relative; }
.yt-pane {
  position: absolute; inset: 0; padding: 52px 24px 24px;
  opacity: 0; visibility: hidden; transform: translateY(10px);
  transition: opacity .4s ease, transform .4s cubic-bezier(.2,.8,.3,1);
  display: flex; flex-direction: column;
}
.yt-pane.on { opacity: 1; visibility: visible; transform: none; }
.yt-notch { position: absolute; top: 14px; left: 50%; transform: translateX(-50%); width: 74px; height: 18px; border-radius: 99px; background: var(--yt-shell); z-index: 4; }
.yt-field { border: 1px solid var(--yt-border); background: var(--yt-field); padding: 14px 15px; }
.yt-bub { display: inline-block; border: 1px solid var(--yt-border); padding: 9px 16px; margin: 0 7px 9px 0; font-size: 13px; font-weight: 500; letter-spacing: .2px; }
.yt-bub.on { border-color: var(--yt-accent); background: var(--yt-cardbg); }

@media (max-width: 900px) {
  .yt-show { grid-template-columns: 1fr; gap: 44px; padding: 0 32px; }
  .yt-phone { order: -1; margin: 0 auto; }
}
@media (max-width: 860px) {
  .yt-deck { height: 500px; }
  .yt-card { --w: 262px; transform-origin: 50% 660px; }
  .yt-back { padding: 30px 26px; }
  .yt-ctrl { margin-top: -34px; }
}
@media (prefers-reduced-motion: reduce) { .yt-card, .yt-inner, .yt-pane { transition: none; } }
`;

export default function CrystalLanding() {
  var [mode, setMode] = useState(function(){try{return localStorage.getItem("yt_theme")||"dark"}catch(e){return"dark"}});
  var t = T[mode];

  var [current, setCurrent] = useState(3);
  var [drag, setDrag] = useState(0);
  var [dragging, setDragging] = useState(false);
  var [step, setStep] = useState(0);
  var [lifted, setLifted] = useState(-1);
  var [tilt, setTilt] = useState({ x: 0, y: 0 });
  var startX = useRef(0);
  var live = useRef(false);
  var moved = useRef(false);
  var SPREAD = 12;
  var PXPER = 120;

  useEffect(function() {
    if (!document.getElementById("cl-fonts")) {
      var link = document.createElement("link");
      link.id = "cl-fonts"; link.rel = "stylesheet"; link.href = FONT_URL;
      document.head.appendChild(link);
    }
    try{localStorage.setItem("yt_theme",mode)}catch(e){}
    document.body.style.background = t.bg;
    document.body.style.margin = "0";
    document.body.style.transition = "background 0.6s";
    window.scrollTo(0, 0);
  }, [mode, t.bg]);

  function go(i) {
    setCurrent(Math.max(0, Math.min(STYLES.length - 1, i)));
    setDrag(0);
  }
  function down(x) { startX.current = x; live.current = true; moved.current = false; setDragging(true); }
  function move(x) {
    if (!live.current) return;
    var d = (startX.current - x) / PXPER;
    if (Math.abs(d) > 0.06) moved.current = true;
    setDrag(d);
  }
  function up(x) {
    if (!live.current) return;
    live.current = false; setDragging(false);
    var target = Math.round(current + (startX.current - x) / PXPER);
    if (moved.current || target !== current) go(target); else setDrag(0);
    setTimeout(function(){ moved.current = false; }, 60);
  }
  useEffect(function() {
    function esc(e) { if (e.key === "Escape") setLifted(-1); }
    document.addEventListener("keydown", esc);
    return function(){ document.removeEventListener("keydown", esc); };
  }, []);

  useEffect(function() {
    function mm(e) { move(e.clientX); }
    function mu(e) { up(e.clientX); }
    window.addEventListener("mousemove", mm);
    window.addEventListener("mouseup", mu);
    return function() {
      window.removeEventListener("mousemove", mm);
      window.removeEventListener("mouseup", mu);
    };
  });

  var pos = current + drag;

  var ctaStyle = {
    display: "inline-block", padding: "20px 54px",
    background: t.btnBg, color: t.btnText, fontSize: 13, fontWeight: 600,
    fontFamily: SANS, letterSpacing: 2.2, textDecoration: "none", textTransform: "uppercase",
  };
  var eyebrowStyle = { fontFamily: SANS, fontSize: 11, fontWeight: 600, letterSpacing: 3.4, color: t.accentSoft, textTransform: "uppercase", margin: "0 0 28px" };

  return (
    <div style={{
      minHeight: "100vh", background: t.grad, fontFamily: SANS, color: t.text,
      transition: "background 0.6s, color 0.6s", isolation: "isolate",
      "--yt-border": t.border, "--yt-textsoft": t.textSoft, "--yt-textmuted": t.textMuted,
      "--yt-accent": t.accent, "--yt-accentsoft": t.accentSoft, "--yt-accentmuted": t.accentMuted,
      "--yt-text": t.text, "--yt-backbg": t.backBg, "--yt-cardbg": t.cardBg,
      "--yt-shell": t.phoneShell, "--yt-screen": t.phoneScreen, "--yt-field": t.field,
      "--yt-frameshadow": t.frameShadow, "--yt-scrim": t.scrim,
    }}>
      <style>{CSS}</style>

      {/* ── NAV ── */}
      <nav style={{ display: "flex", justifyContent: "space-between", alignItems: "center", padding: "36px 48px", maxWidth: 1200, margin: "0 auto" }}>
        <span style={{ fontFamily: SANS, fontSize: 11, fontWeight: 600, letterSpacing: 3.4, color: t.textSoft, textTransform: "uppercase" }}>Yours Truly</span>
        <div style={{ display: "flex", gap: 24, alignItems: "center" }}>
          <button onClick={function(){setMode(mode==="dark"?"light":"dark")}} style={{ background: "none", border: "none", padding: 0, cursor: "pointer", fontSize: 11, fontWeight: 600, letterSpacing: 2.4, color: t.textMuted, fontFamily: SANS, textTransform: "uppercase" }}>{mode === "dark" ? "light" : "dark"}</button>
          <a href="/new-constellation" style={{ fontFamily: SANS, fontSize: 11, fontWeight: 600, color: t.text, textDecoration: "none", letterSpacing: 2.4, textTransform: "uppercase", borderBottom: "1px solid " + t.accentMuted, paddingBottom: 3 }}>{CTA}</a>
        </div>
      </nav>

      {/* ── HERO ── */}
      <section style={{ textAlign: "center", padding: "110px 32px 40px", maxWidth: 820, margin: "0 auto", animation: "cl-fade 1.2s ease-out" }}>
        <p style={eyebrowStyle}>A House of Memory</p>
        <h1 style={{ fontFamily: PLAY, fontWeight: 400, fontSize: "clamp(38px, 7vw, 64px)", lineHeight: 1.12, letterSpacing: 2, margin: "0 0 20px", color: t.text }}>
          The words they'd say
        </h1>
        <h1 style={{ fontFamily: PLAY, fontWeight: 400, fontSize: "clamp(38px, 7vw, 64px)", lineHeight: 1.12, letterSpacing: 2, margin: "0 0 36px", fontStyle: "italic", color: t.accent }}>
          at the podium.
        </h1>
        <p style={{ fontFamily: SERIF, fontSize: 18, fontWeight: 400, color: t.textSoft, lineHeight: 1.85, maxWidth: 520, margin: "0 auto 48px", letterSpacing: 0.3 }}>
          You send one link. Their people write a memory. We print it as a framed poster and post it to your door.
        </p>
        <a href="/new-constellation" className="cl-btn" style={ctaStyle}>{CTA}</a>
        <p style={{ marginTop: 22, fontSize: 13, color: t.textMuted, fontWeight: 400, letterSpacing: .6, fontFamily: SERIF }}>Free to create · Poster $49 · Framed $79</p>
      </section>

      {/* ── FANNED POSTER DECK ── */}
      <section style={{ padding: "30px 0 110px", animation: "cl-fade 1.5s ease-out" }}>
        <div
          className={"yt-deck" + (dragging ? " is-drag" : "")}
          onMouseDown={function(e){ down(e.clientX); e.preventDefault(); }}
          onMouseMove={function(e){
            if (live.current) return;
            var r = e.currentTarget.getBoundingClientRect();
            setTilt({
              x: ((e.clientX - r.left) / r.width - 0.5) * 2,
              y: ((e.clientY - r.top) / r.height - 0.5) * 2,
            });
          }}
          onMouseLeave={function(){ setTilt({ x: 0, y: 0 }); }}
          onTouchStart={function(e){ down(e.touches[0].clientX); }}
          onTouchMove={function(e){ move(e.touches[0].clientX); }}
          onTouchEnd={function(e){ up(e.changedTouches[0].clientX); }}
        >
          <div className="yt-fan">
            {STYLES.map(function(s, i) {
              var d = i - pos, a = Math.abs(d);
              var isLive = (i === current && !dragging);
              return (
                <button key={s.file} type="button"
                  className={"yt-card" + (isLive ? " is-live" : "")}
                  onClick={function(e){
                    e.stopPropagation();
                    if (moved.current) return;
                    if (i === current) setLifted(i); else go(i);
                  }}
                  style={{
                    transform: "rotate(" + (d * SPREAD) + "deg) translateY(" + (a * 10) + "px)",
                    zIndex: 100 - Math.round(a * 10),
                    opacity: a > 3.4 ? 0 : Math.max(0.42, 1 - a * 0.16),
                  }}>
                  <span className="yt-inner" style={isLive ? {
                    transform: "rotateY(" + (tilt.x * 7).toFixed(2) + "deg) rotateX(" + (-tilt.y * 5).toFixed(2) + "deg)",
                  } : null}>
                    <span className="yt-face yt-front" style={{ boxShadow: t.frameShadow }}>
                      <img src={"/samples/style-" + s.file + ".png"}
                        alt={s.name + " — a framed constellation poster made from six people's words about Eleanor Mensah"}
                        loading="lazy" draggable="false" />
                      <span className="yt-sheen" style={{ transform: "translateX(" + (tilt.x * 34).toFixed(1) + "%)" }} />
                    </span>
                  </span>
                </button>
              );
            })}
          </div>
        </div>

        <div className="yt-ctrl">
          <button className="yt-arrow" type="button" aria-label="Previous style" disabled={current <= 0} onClick={function(){go(current - 1)}}>&#8249;</button>
          <div className="yt-dots">
            {STYLES.map(function(s, i) {
              return <button key={s.file} type="button" aria-label={s.name} className={"yt-dot" + (i === Math.round(pos) ? " on" : "")} onClick={function(){go(i)}} />;
            })}
          </div>
          <button className="yt-arrow" type="button" aria-label="Next style" disabled={current >= STYLES.length - 1} onClick={function(){go(current + 1)}}>&#8250;</button>
        </div>

        <p style={{ fontFamily: SANS, fontSize: 11, fontWeight: 600, letterSpacing: 2.4, textTransform: "uppercase", color: t.textSoft, marginTop: 34, textAlign: "center" }}>
          {STYLES[Math.round(pos)] ? STYLES[Math.round(pos)].name : ""}
        </p>
        <p style={{ fontFamily: SERIF, fontStyle: "italic", fontSize: 15, fontWeight: 400, color: t.textMuted, marginTop: 10, letterSpacing: 1, textAlign: "center", padding: "0 32px" }}>
          Six people wrote about Eleanor. Drag the stack — tap a poster to lift it off the wall.
        </p>
      </section>

      {/* ── LIFTED POSTER ── */}
      <div className={"yt-lift" + (lifted > -1 ? " on" : "")}
           onClick={function(){ setLifted(-1); }}
           role="dialog" aria-modal="true">
        <button className="yt-close" type="button" aria-label="Close" onClick={function(){ setLifted(-1); }}>&#10005;</button>
        {lifted > -1 ? (
          <div className="yt-lift-poster" onClick={function(e){ e.stopPropagation(); }}>
            <img src={"/samples/style-" + STYLES[lifted].file + ".png"} alt={STYLES[lifted].name + " poster"} />
          </div>
        ) : null}
        {lifted > -1 ? (
          <div className="yt-lift-text" onClick={function(e){ e.stopPropagation(); }}>
            <p style={{ fontFamily: SANS, fontSize: 11, fontWeight: 600, letterSpacing: 2.6, textTransform: "uppercase", color: t.accentSoft, margin: "0 0 14px" }}>{STYLES[lifted].desc}</p>
            <p style={{ fontFamily: PLAY, fontSize: 38, fontWeight: 400, letterSpacing: 1, color: t.text, margin: "0 0 22px" }}>{STYLES[lifted].name}</p>
            <div style={{ width: 44, height: 1, background: t.accentMuted, margin: "0 0 22px" }} />
            <p style={{ fontFamily: SERIF, fontSize: 18, fontWeight: 400, lineHeight: 1.8, color: t.textSoft, margin: "0 0 34px" }}>{STYLES[lifted].back}</p>
            <a href="/new-constellation" className="cl-btn" style={ctaStyle}>{CTA}</a>
          </div>
        ) : null}
      </div>

      {/* ── WHAT YOU'RE SENDING THEM ── */}
      <section style={{ background: t.band, padding: "120px 0", borderTop: "1px solid " + t.border, borderBottom: "1px solid " + t.border }}>
        <div style={{ textAlign: "center", padding: "0 48px" }}>
          <p style={eyebrowStyle}>What you're sending them</p>
          <h2 style={{ fontFamily: PLAY, fontWeight: 400, fontSize: "clamp(28px, 4.5vw, 40px)", letterSpacing: 1, margin: "0 0 20px", color: t.text }}>
            Three questions. Ninety seconds.
          </h2>
          <p style={{ fontFamily: SERIF, fontStyle: "italic", fontSize: 19, fontWeight: 400, color: t.textSoft, lineHeight: 1.8, maxWidth: 480, margin: "0 auto" }}>
            No account, nothing to install. This is the whole thing your friends see.
          </p>
        </div>

        <div className="yt-show">
          <div className="yt-steps">
            {STEPS.map(function(s, i) {
              return (
                <button key={s.t} type="button" className={"yt-step" + (step === i ? " on" : "")} onClick={function(){setStep(i)}}>
                  <span style={{ display: "block", fontFamily: SANS, fontSize: 11, fontWeight: 600, letterSpacing: 2.6, textTransform: "uppercase", color: step === i ? t.accent : t.textMuted, marginBottom: 10 }}>
                    {["One", "Two", "Three"][i]}
                  </span>
                  <span style={{ display: "block", fontFamily: PLAY, fontSize: 22, fontWeight: 400, letterSpacing: .5, color: t.text, marginBottom: 8 }}>{s.t}</span>
                  <span style={{ display: "block", fontFamily: SERIF, fontSize: 16, fontWeight: 400, lineHeight: 1.7, color: t.textSoft }}>{s.s}</span>
                </button>
              );
            })}
          </div>

          <div className="yt-phone">
            <span className="yt-notch" />
            <div className="yt-screen">

              <div className={"yt-pane" + (step === 0 ? " on" : "")}>
                <p style={{ fontFamily: SANS, fontSize: 10, fontWeight: 600, letterSpacing: 2.4, textTransform: "uppercase", color: t.accentSoft, margin: "0 0 20px" }}>Yours Truly</p>
                <p style={{ fontFamily: PLAY, fontSize: 21, fontWeight: 400, lineHeight: 1.3, color: t.text, margin: 0 }}>Say something kind about Eleanor</p>
                <p style={{ fontFamily: SERIF, fontSize: 14.5, fontWeight: 400, lineHeight: 1.7, color: t.textSoft, margin: "12px 0 0" }}>
                  A memory, a moment, or what you'd say if someone handed you the mic. In your own words.
                </p>
                <div className="yt-field" style={{ flex: 1, marginTop: 20 }}>
                  <p style={{ fontFamily: SERIF, fontStyle: "italic", fontSize: 15.5, fontWeight: 400, lineHeight: 1.7, color: t.textSoft, margin: 0 }}>
                    "Forty years of Sunday calls and she never picked up on the second ring."
                  </p>
                </div>
              </div>

              <div className={"yt-pane" + (step === 1 ? " on" : "")}>
                <p style={{ fontFamily: SANS, fontSize: 10, fontWeight: 600, letterSpacing: 2.4, textTransform: "uppercase", color: t.accentSoft, margin: "0 0 20px" }}>Yours Truly</p>
                <p style={{ fontFamily: PLAY, fontSize: 21, fontWeight: 400, lineHeight: 1.3, color: t.text, margin: 0 }}>What words describe Eleanor?</p>
                <p style={{ fontFamily: SERIF, fontSize: 14.5, fontWeight: 400, lineHeight: 1.7, color: t.textSoft, margin: "12px 0 22px" }}>Choose up to six.</p>
                <div>
                  {ADJECTIVES.map(function(a) {
                    return <span key={a} className={"yt-bub" + (PICKED[a] ? " on" : "")} style={{ color: PICKED[a] ? t.accent : t.textSoft }}>{a}</span>;
                  })}
                </div>
              </div>

              <div className={"yt-pane" + (step === 2 ? " on" : "")}>
                <p style={{ fontFamily: SANS, fontSize: 10, fontWeight: 600, letterSpacing: 2.4, textTransform: "uppercase", color: t.accentSoft, margin: "0 0 20px" }}>Yours Truly</p>
                <p style={{ fontFamily: PLAY, fontSize: 21, fontWeight: 400, lineHeight: 1.3, color: t.text, margin: 0 }}>An inside joke, or a wish for them?</p>
                <p style={{ fontFamily: SERIF, fontSize: 14.5, fontWeight: 400, lineHeight: 1.7, color: t.textSoft, margin: "12px 0 0" }}>
                  Something that'd make them laugh, or something you hope for them. Totally optional.
                </p>
                <div className="yt-field" style={{ flex: 1, marginTop: 20 }}>
                  <p style={{ fontFamily: SERIF, fontStyle: "italic", fontSize: 15.5, fontWeight: 400, lineHeight: 1.7, color: t.textMuted, margin: 0 }}>
                    Or just what you hope for them
                  </p>
                </div>
                <div style={{ display: "flex", gap: 10, marginTop: 16 }}>
                  <span style={{ flex: 1, textAlign: "center", padding: "12px 0", border: "1px solid " + t.border, fontSize: 11, fontWeight: 600, letterSpacing: 1.6, textTransform: "uppercase", color: t.textMuted }}>Skip</span>
                  <span style={{ flex: 1, textAlign: "center", padding: "12px 0", background: t.btnBg, color: t.btnText, fontSize: 11, fontWeight: 600, letterSpacing: 1.6, textTransform: "uppercase" }}>Submit</span>
                </div>
              </div>

            </div>
          </div>
        </div>
      </section>

      {/* ── HOW IT WORKS ── */}
      <section style={{ padding: "140px 48px", maxWidth: 1000, margin: "0 auto" }}>
        <p style={{ textAlign: "center", fontFamily: SANS, fontSize: 11, fontWeight: 600, letterSpacing: 3.4, color: t.accentSoft, textTransform: "uppercase", marginBottom: 72 }}>The Process</p>
        <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fit, minmax(260px, 1fr))", gap: 32 }}>
          {[
            { n: "I", t: "Gather", d: "Create a constellation and share the link. Friends, family and colleagues each answer the three questions above." },
            { n: "II", t: "Compose", d: "Every word is read and distilled. Each voice gets a star, in their own words — nothing invented, nothing generated." },
            { n: "III", t: "Frame", d: "Choose a style. Order the print. A one-of-a-kind framed poster arrives at your door, ready to give." },
          ].map(function(s) {
            return (
              <div key={s.n} className="cl-card" style={{ padding: "44px 32px", border: "1px solid " + t.border, background: t.cardBg }}>
                <p style={{ fontFamily: SANS, fontSize: 12, fontWeight: 700, letterSpacing: 2, color: t.accentSoft, margin: "0 0 24px" }}>{s.n}</p>
                <p style={{ fontFamily: PLAY, fontSize: 24, fontWeight: 400, color: t.text, margin: "0 0 20px", letterSpacing: 1 }}>{s.t}</p>
                <p style={{ fontFamily: SERIF, fontSize: 16, fontWeight: 400, color: t.textSoft, lineHeight: 1.75, margin: 0 }}>{s.d}</p>
              </div>
            );
          })}
        </div>
      </section>

      {/* ── THE OCCASIONS ── */}
      <section style={{ padding: "0 48px 140px", maxWidth: 700, margin: "0 auto", textAlign: "center" }}>
        <p style={{ fontFamily: SERIF, fontStyle: "italic", fontSize: "clamp(20px, 3vw, 26px)", fontWeight: 400, color: t.textSoft, lineHeight: 2.4, margin: "0 0 48px" }}>
          For the executive who built the team.<br />
          The friend who never asks for anything back.<br />
          The teacher who saw you before you could see yourself.
        </p>
        <div style={{ display: "flex", flexWrap: "wrap", gap: 12, justifyContent: "center" }}>
          {["Farewell", "Birthday", "Retirement", "Memorial", "Thank You", "Anniversary", "Graduation", "Just Because"].map(function(tag) {
            return <span key={tag} style={{ padding: "10px 24px", border: "1px solid " + t.border, fontSize: 12, fontWeight: 500, color: t.textSoft, fontFamily: SANS, letterSpacing: 1.4, textTransform: "uppercase" }}>{tag}</span>;
          })}
        </div>
      </section>

      {/* ── PRICING ── */}
      <section style={{ background: t.band, borderTop: "1px solid " + t.border, borderBottom: "1px solid " + t.border, padding: "120px 48px" }}>
        <div style={{ maxWidth: 760, margin: "0 auto", textAlign: "center" }}>
          <p style={eyebrowStyle}>What it costs</p>
          <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fit, minmax(240px, 1fr))", gap: 24, marginTop: 12 }}>
            <div style={{ padding: "48px 32px", border: "1px solid " + t.border }}>
              <p style={{ fontFamily: SANS, fontSize: 11, fontWeight: 600, letterSpacing: 2.6, color: t.textMuted, textTransform: "uppercase", margin: "0 0 20px" }}>Poster</p>
              <p style={{ fontFamily: PLAY, fontSize: 46, fontWeight: 400, color: t.text, margin: 0, letterSpacing: 1 }}>$49</p>
              <p style={{ fontFamily: SERIF, fontSize: 15, fontWeight: 400, color: t.textSoft, lineHeight: 1.8, margin: "18px 0 0" }}>Premium luster print<br />12 × 12, unframed</p>
            </div>
            <div style={{ padding: "48px 32px", border: "1px solid " + t.accentMuted }}>
              <p style={{ fontFamily: SANS, fontSize: 11, fontWeight: 600, letterSpacing: 2.6, color: t.accentSoft, textTransform: "uppercase", margin: "0 0 20px" }}>Framed</p>
              <p style={{ fontFamily: PLAY, fontSize: 46, fontWeight: 400, color: t.text, margin: 0, letterSpacing: 1 }}>$79</p>
              <p style={{ fontFamily: SERIF, fontSize: 15, fontWeight: 400, color: t.textSoft, lineHeight: 1.8, margin: "18px 0 0" }}>Enhanced matte paper<br />Slim black frame</p>
            </div>
          </div>
          <p style={{ fontFamily: SERIF, fontStyle: "italic", fontSize: 16.5, fontWeight: 400, color: t.textMuted, margin: "36px 0 0" }}>
            Creating and gathering is free. You only pay if you like what comes out.
          </p>
        </div>
      </section>

      {/* ── CLOSER ── */}
      <section style={{ textAlign: "center", padding: "140px 48px", maxWidth: 620, margin: "0 auto" }}>
        <p style={{ fontFamily: SERIF, fontStyle: "italic", fontSize: "clamp(24px, 4vw, 36px)", fontWeight: 400, color: t.textSoft, lineHeight: 1.8, margin: "0 0 48px" }}>
          Some people deserve more<br />than a card.
        </p>
        <a href="/new-constellation" className="cl-btn" style={ctaStyle}>{CTA}</a>
        <p style={{ marginTop: 22, fontSize: 13, color: t.textMuted, fontWeight: 400, letterSpacing: .6, fontFamily: SERIF }}>Free to create · Nothing to install</p>
      </section>

      {/* ── FOOTER ── */}
      <footer style={{ textAlign: "center", padding: "60px 48px 80px", borderTop: "1px solid " + t.border }}>
        <p style={{ fontFamily: SANS, fontSize: 11, fontWeight: 600, letterSpacing: 3.4, color: t.textMuted, margin: "0 0 10px", textTransform: "uppercase" }}>Yours Truly</p>
        <p style={{ fontFamily: SERIF, fontSize: 14, fontWeight: 400, color: t.textMuted, margin: 0 }}>Built with love in Atlanta</p>
      </footer>
    </div>
  );
}
